import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	Popover,
	Typography,
	Link
} from '@material-ui/core';

import ResponsiveDialog from "./ResponsiveDialog";
import Constants from "../utils/Constants";

const styles = makeStyles(theme => ({
	icon: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		color: theme.palette.primary.main
	},

	pointer: {
		cursor: "pointer"
	},

	dialogPaper: {
		minWidth: 290,
		minHeight: 420
	},

	popover: {
		pointerEvents: "none"
	},

	popoverPaper: {
		width: 300,
		padding: theme.spacing(2)
	}
}));

function MoreInfo(props) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [popupOpen, setPopupOpen] = useState(false);
	const [popoverOpen, setPopoverOpen] = useState(false);

	const clickHandler = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setPopupOpen(true);
	}, [setPopupOpen]);

	const showPopover = (event) => {
		setPopoverOpen(true);
		setAnchorEl(event.target);
	};

	const closePopover = () => {
		setAnchorEl(null);
		setPopoverOpen(false);
	};

	const keyDownHandler = (ev) => {
		if (ev.keyCode === 32 || ev.keyCode === 13) { // space or enter
			if (props.content) {
				clickHandler();
			} else if (props.hover) {
				showPopover();
			}
		} else if (ev.keyCode === 27) { // esc
			if (props.content) {
				clickHandler();
			} else if (props.hover) {
				closePopover();
			}
		}
	};

	const closePopup = (e) => {
		setPopupOpen(false);
	};

	let classes = styles(),
		{textToUse, content, title, hover, hoverTitle, hoverContent, maxWidth} = props,
		isMobile = Constants.ENV.IS_MOBILE,
		hTitle = hoverTitle || title,
		hContent = hoverContent || content;

	return (
		<React.Fragment>
			<span
				onMouseEnter={hover ? showPopover : null}
				onMouseLeave={hover ? closePopover : null}
				onMouseDown={isMobile && hover ? showPopover : null}
				onMouseUp={isMobile && hover ? closePopover : null}
				onClick={content && !textToUse ? clickHandler : null}
				tabIndex={0}
				role="button"
				aria-label={hoverTitle || title}
				onKeyDown={content ? keyDownHandler : null}>
				{textToUse ? (
					<Link
						onClick={clickHandler}
						className={classes.pointer}
						aria-label={`learn more about ${title}`}>{textToUse}</Link>
				) : (
					<FontAwesomeIcon
						icon={["far", "question-circle"]}
						title={hoverTitle || title}
						className={`${classes.icon} ${content ? classes.pointer : ""}`} />
				)}
			</span>
			<ResponsiveDialog
				open={popupOpen}
				title={title}
				content={content}
				maxWidth={maxWidth}
				closeHandler={closePopup} />
			{hover ?
				<Popover
					open={popoverOpen}
					anchorEl={anchorEl}
					onClose={closePopover}
					className={classes.popover}
					classes={{paper: classes.popoverPaper}}
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}
					transformOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}>
					{hTitle ?
						typeof hTitle === "string" ?
							<Typography variant="h1">{hTitle}</Typography>
							:
							hTitle
						: null
					}
					{hContent ?
						typeof hContent === "string" ?
							<Typography>{hContent}</Typography>
							:
							hTitle
						: null
					}
				</Popover>
				: null
			}
		</React.Fragment>
	);
}

MoreInfo.propTypes = {
	textToUse: PropTypes.string,
	title: PropTypes.string,
	content: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	hover: PropTypes.bool,
	hoverTitle: PropTypes.string,
	hoverContent: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	maxWidth: PropTypes.string
};

export default MoreInfo;
